/**
 * # Card Number Input
 * An input container for card number payment details.
 *
 * @example
 * new Spark.Payments.CardNumber(el, {...});
 *
 * @module components/payments.js
 */

import BaseComponent from "../base";
import TypeAhead from "../typeahead";

import addClass from "../../helpers/dom/add-class";
import removeClass from "../../helpers/dom/remove-class";
import makeElement from "../../helpers/dom/make-element";

import assign from "../../helpers/polyfill/assign";
import repeat from "../../helpers/polyfill/repeat";
import startsWith from "../../helpers/polyfill/starts-with";

import each from "../../helpers/util/each";
import isInteger from "../../helpers/util/is-integer";
import mixin from "../../helpers/util/mixin";

import messaging from "../../mixins/messaging";
import validation from "../../mixins/validation";

let onCardTypeEvent;
let onValidEvent;
let onInvalidEvent;

const cardDefaults = {
  amex: {
    name: "American Express",
    length: 15,
    cvv: 4,
    prefix: [34, 37],
    pattern: [4, 6, 5],
  },
  china: {
    name: "China UnionPay",
    length: [16, 19],
    cvv: 3,
    prefix: 62,
    pattern: [
      [4, 4, 4, 4],
      [6, 13],
    ],
  },
  discover: {
    name: "Discover",
    length: 16,
    cvv: 3,
    prefix: [6011, [622126, 622925], [644, 649], 65],
    pattern: [4, 4, 4, 4],
  },
  jcb: {
    name: "JCB",
    length: 16,
    cvv: 3,
    prefix: [[3528, 3589]],
    pattern: [4, 4, 4, 4],
  },
  mc: {
    name: "MasterCard",
    length: 16,
    cvv: 3,
    prefix: [[51, 55], [2221, 2720]],
    pattern: [4, 4, 4, 4],
  },
  uatp: {
    name: "UATP",
    length: 15,
    cvv: 3,
    prefix: 1,
    pattern: [4, 5, 6],
  },
  dc: {
    name: "Diners Club International",
    length: 14,
    cvv: 3,
    prefix: [[300, 305], 309, 36, [38, 39]],
    pattern: [4, 6, 4],
  },
  visa: {
    name: "Visa",
    length: [16, 19],
    cvv: 3,
    prefix: [4, 4026, 417500, 4405, 4508, 4844, 4913, 4917],
    pattern: [
      [4, 4, 4, 4],
      [6, 4, 8, 1],
    ],
  },
};

const unflipCard = function () {
  this.el.dataset.cardType = this.type;
  this.multiPattern = false;
  this.patternIndex = false;
  this.type = "";
  this._initTypeAhead();
};

const patternDefaults = {
  placeholder: "0000 0000 0000 0000",
  minlength: 19,
  maxlength: 19,
  pattern: [4, 4, 4, 4],
  validation: "\\d\\d\\d\\d ?\\d\\d\\d\\d ?\\d\\d\\d\\d ?\\d\\d\\d\\d",
  typeaheadformat: "\\d\\d\\d\\d \\d\\d\\d\\d \\d\\d\\d\\d \\d\\d\\d\\d",
};

class CardNumber extends BaseComponent {
  /**
   * CardNumber constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();
    this._initTypeAhead();
    this._currentCardProperties = Object.assign({}, patternDefaults);
    this._currentPrefix = 0;

    onCardTypeEvent = document.createEvent("Event");
    onValidEvent = document.createEvent("Event");
    onInvalidEvent = document.createEvent("Event");

    onCardTypeEvent.initEvent("type", true, true);
    onValidEvent.initEvent("valid", true, true);
    onInvalidEvent.initEvent("invalid", true, true);
  }

  /**
   * Parse parameters from the elements.
   */
  _parseParams() {
    if (this.disableAllCardDefaults) {
      if (this.cardTypes === null || Object.keys(this.cardTypes).length === 0) {
        throw new Error(
          "cardTypes param cannot be null or empty when setting disableAllCardDefaults to true.",
          this.el
        );
      }

      this._cardTypes = this.cardTypes;
    } else {
      if (this.disableCardDefaults) {
        const disabledDefaults = this.disableCardDefaults;
        const enabledDefaults = {};
        const defaults = Object.keys(cardDefaults).filter((config) => !disabledDefaults.some((disabled) => disabled === config));

        each(defaults, (config) => (enabledDefaults[config] = assign({}, cardDefaults[config])));

        this._cardTypes = assign(enabledDefaults, this.cardTypes || {});
      } else {
        this._cardTypes = assign(cardDefaults, this.cardTypes || {});
      }
    }
  }

  /**
   * Sets up references
   * @param {Element} el base DOM element
   */
  _cacheElements(el) {
    this.el = el;
    this.inputEl = el.querySelector("input");

    // Type needs to be tel as Android Gboard doesn't send proper keycodes to events on mobile chrome
    // More info: https://bugs.chromium.org/p/chromium/issues/detail?id=118639 (2012 - still open)
    this.inputEl.setAttribute("type", "tel");

    if (this.inputEl.value) {
      addClass(this.el, "active");
    }

    this.messageEl = this.el.querySelector(".spark-input__message") || makeElement('<span class="spark-input__message"></span>');
  }

  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onInputBound = this._onInput.bind(this);
    this._onCardTypeBound = this._onCardType.bind(this);
    this._onBlurBound = this._onBlur.bind(this);
    this._onFocusBound = this._onFocus.bind(this);
    this._onInvalidBound = this._onInvalid.bind(this);
    this._onValidBound = this._onValid.bind(this);
  }

  /**
   * Add event listeners for DOM events.
   */
  _addEventListeners() {
    this.inputEl.addEventListener("input", this._onInputBound, false);
    this.inputEl.addEventListener("type", this._onCardTypeBound, false);
    this.inputEl.addEventListener("blur", this._onBlurBound, false);
    this.inputEl.addEventListener("focus", this._onFocusBound, false);
    this.inputEl.addEventListener("invalid", this._onInvalidBound, false);
    this.inputEl.addEventListener("valid", this._onValidBound, false);
  }

  /**
   * Remove event listeners for DOM events..
   */
  _removeEventListeners() {
    this.inputEl.removeEventListener("input", this._onInputBound, false);
    this.inputEl.removeEventListener("type", this._onCardTypeBound, false);
    this.inputEl.removeEventListener("blur", this._onBlurBound, false);
    this.inputEl.removeEventListener("focus", this._onFocusBound, false);
    this.inputEl.removeEventListener("invalid", this._onInvalidBound, false);
    this.inputEl.removeEventListener("valid", this._onValidBound, false);
  }

  /**
   * onInvalid method for firing provided callbacks
   */
  _onInvalid() {
    this.onInvalid.call(this);
  }

  /**
   * onValid method for firing provided callbacks
   */
  _onValid() {
    this.onValid.call(this);
  }

  /**
   * onBlur callback, for controlling component active state, firing provided callbacks, and running validation
   */
  _onBlur() {
    if (!this.inputEl.value) {
      removeClass(this.el, "active");
    }

    this.onBlur.call(this);
  }

  /**
   * onFocus callback, for controlling component active state and firing provided callbacks
   */
  _onFocus() {
    addClass(this.el, "active");
    this.onFocus.call(this);
  }

  /**
   * onCardType callback for controlling the displayed card type once a matching pattern has been recognized
   */
  _onCardType() {
    if (this.type) {
      const cardType = this._cardTypes[this.type];
      this.multiPattern = cardType.pattern.some((patternValue) => Array.isArray(patternValue));
    } else {
      this.multiPattern = false;
    }

    this.onCardType.call(this);
  }

  /**
   * Updates typeahead based on cardtype
   */
  _updateTypeAhead() {
    this.inputEl.setAttribute("placeholder", this._currentCardProperties.placeholder);
    this.inputEl.setAttribute("minlength", this._currentCardProperties.minlength);
    this.inputEl.setAttribute("maxlength", this._currentCardProperties.maxlength);
    this.inputEl.setAttribute("pattern", this._currentCardProperties.pattern);
    this.inputEl.dataset.typeaheadFormat = this._currentCardProperties.typeaheadformat;

    this.typeahead.format = this.typeahead._parseFormat(this._currentCardProperties.typeaheadformat);
    this.typeahead._parseParams();
    this.typeahead.remove(true);

    let placeholderEl = this.el.querySelector(".spark-input__placeholder");
    placeholderEl.parentNode.removeChild(placeholderEl);

    this.typeahead = new TypeAhead(this.el);
    this.typeahead.updateValue();
  }

  /**
   * Initialize typeahead based on card type
   */
  _initTypeAhead() {
    if (!this.typeahead) {
      this.multiPattern = false;
      this.inputEl.setAttribute("placeholder", patternDefaults.placeholder);
      this.inputEl.setAttribute("minlength", patternDefaults.minlength);
      this.inputEl.setAttribute("maxlength", patternDefaults.maxlength);
      this.inputEl.setAttribute("pattern", patternDefaults.validation);
      this.inputEl.dataset.typeaheadFormat = patternDefaults.typeaheadformat;
      this.typeahead = new TypeAhead(this.el);
    }
  }

  /**
   * Get cardType for input value
   */
  _getCardType() {
    let cardTypeMatched = false;

    // Loop over card type codes to pull type data and compare to value
    // Cards can have different prefixes; 62* can be Bank Of China unless it's in the range of [622126 - 622925] which is Discover
    for (const type in this._cardTypes) {
      const cardType = this._cardTypes[type];

      if (isInteger(cardType.prefix)) {
        if (startsWith(this.value, cardType.prefix.toString())) {
          if (type !== this.type) {
            this.type = type;
            this._currentPrefix = cardType.prefix;
          }
          cardTypeMatched = true;
        }
      } else {
        for (const prefixIndex in cardType.prefix) {
          if (isInteger(cardType.prefix[prefixIndex])) {
            if (startsWith(this.value, cardType.prefix[prefixIndex].toString())) {
              this.type = type;
              this._currentPrefix = cardType.prefix[prefixIndex];
              cardTypeMatched = true;
            }
          } else {
            const rangeStart = cardType.prefix[prefixIndex][0];
            const rangeEnd = cardType.prefix[prefixIndex][1];
            const valuePrefixSample = parseInt(this.value.substring(0, rangeEnd.toString().length), 10);

            if (valuePrefixSample >= rangeStart && valuePrefixSample <= rangeEnd) {
              this.type = type;
              this._currentPrefix = valuePrefixSample;
              cardTypeMatched = true;
            }
          }
        }
      }

      if (cardTypeMatched === false) {
        this.type = "";
        this._currentPrefix = 0;
      }
    }
  }

  /**
   * Get cardPattern based off of current card Type
   */
  _getCardPatterns() {
    const cardType = this._cardTypes[this.type];

    // pattern is an array like [4, 4, 4, 4] that represents number grouping
    let pattern = patternDefaults.pattern;

    // regexPattern converts pattern into a regex like //d//d//d//d etc.
    let regexPattern = patternDefaults.typeaheadformat;

    // typeaheadFormat is the regex used in the typeahed component.
    let typeaheadFormat = patternDefaults.typeaheadformat;

    if (this.multiPattern) {
      // Identify which pattern to used based off current value length
      const usePattern = (length) => this.value.length <= length;

      let patternIndex =
        this._cardTypes[this.type].length.findIndex(usePattern);

      // If there the value is longer than largest option, uses the largest option
      if (patternIndex === -1) {
        patternIndex = this._cardTypes[this.type].length.length - 1;
      }

      pattern = cardType.pattern[patternIndex];
      regexPattern = pattern.map((digits) => repeat("\\d", digits)).join(" ");
      typeaheadFormat = regexPattern;

      // Add an extra digit to the typeahead if it's multi-pattern and not the last pattern
      if (patternIndex !== cardType.pattern.length - 1) {
        typeaheadFormat += "\\d";
      }
    } else if (cardType) {
      pattern = cardType.pattern;
      regexPattern = pattern.map((digits) => repeat("\\d", digits)).join(" ");
      typeaheadFormat = regexPattern;
    }

    this._currentCardProperties.placeholder = pattern.slice(0).map((digits) => repeat("0", digits)).join(" ");
    this._currentCardProperties.maxlength = this._currentCardProperties.placeholder.length;
    this._currentCardProperties.minlength = this._currentCardProperties.placeholder.length;
    this._currentCardProperties.typeaheadformat = typeaheadFormat;
    this._currentCardProperties.pattern = regexPattern;
  }

  /**
   * Update typeahead, component active state, determining the card type, and formatting the display
   * Typeahead will trigger the onInput event
   */

  _updateInput() {
    const currentPrefix = this._currentPrefix;
    const currentPattern = this._currentCardProperties.pattern;

    if (this.value) {
      addClass(this.el, "active");
      this._getCardType();
      this._getCardPatterns();

      // If the start of card number updated, or the current pattern changed, update the typeahead
      if (this._currentPrefix !== currentPrefix || this._currentCardProperties.pattern !== currentPattern) {
        this._updateTypeAhead();
      }
    } else {
      this.type = null;
    }

    if (!this.type) {
      unflipCard.call(this);
    }
  }

  /**
   * Validate the value using the Luhn algorithm, tests for valid credit card number combinition
   * @returns boolean
   */
  _runLuhnTest() {
    let nCheck = 0;
    let nDigit = 0;
    let bEven = false;
    let value = this.value;
    value = value.replace(/\D/g, "");

    for (var n = value.length - 1; n >= 0; n--) {
      let cDigit = value.charAt(n);
      nDigit = parseInt(cDigit, 10);

      if (bEven) {
        if ((nDigit *= 2) > 9) nDigit -= 9;
      }

      nCheck += nDigit;
      bEven = !bEven;
    }

    return nCheck % 10 === 0;
  }

  /**
   * onInput callback, for controlling component active state, firing provided callbacks, running validation,
   * determining the card type, and formatting the display
   */
  _onInput(e) {

    // Pasting needs to update TypeAhead inorder to format correctly
    if (e.inputType === 'insertFromPaste') {
      this._updateTypeAhead();
    }

    this._updateInput();
    this.onInput.call(this);
    this.validate();
  }

  /**
   * Getter that returns the currently recognized card type code
   * @return {String}
   */
  get type() {
    if (this.activeCardType) {
      return this.activeCardType;
    }

    return undefined;
  }

  /**
   * Setter for changing the currently recognized card type code, and updating the card type graphic
   */
  set type(cardType) {
    if (cardType && cardType !== this.activeCardType) {
      this.previousPattern = this.activePattern;
      this.activePattern = this._cardTypes[cardType].pattern;
      this.activeCardType = cardType;
      this.el.dataset.cardType = "cc-" + cardType;

      if (this.inputEl) {
        this.inputEl.dispatchEvent(onCardTypeEvent);
      }
    } else if (!cardType) {
      this.previousPattern = this.activePattern;
      this.activePattern = undefined;
      this.activeCardType = "";
      this.multiPattern = false;
    }
  }

  /**
   * Getter for Primary Component Value
   * @return {String}
   */
  get value() {
    return this.inputEl.value.replace(/\D/g, "");
  }

  /**
   * Setter for Primary Component Value
   * @param {Number} val
   */
  set value(val) {

    if (val.split(' ').join('') === this.inputEl.value.split(' ').join('')) {
      return;
    }

    this.inputEl.value = val;

    // Run input event;
    this._updateInput();
  }

  /**
   * Getter for Valid state, returns true if component value is valid, false if not
   * Runs Luhn Algorithm
   * @return {Boolean}
   */
  get valid() {
    let value = this.value;

    if (/[^0-9-\s]+/.test(value)) return false;

    const input = this.inputEl;
    const format = new RegExp(input.getAttribute("pattern"));
    const _passedLuhnTest = this.useLuhnTest ? this._runLuhnTest() : true;

    return (
      input.willValidate &&
      input.value.length >= input.getAttribute("minlength") &&
      format.test(input.value) &&
      _passedLuhnTest
    );
  }

  /**
   * Method for triggering focus on component's input element from outside of component
   */
  focus() {
    this.inputEl.focus();
  }

  /**
   * Default validate function. This can be overridden by passing a
   * custom validate method as a parameter.
   */
  validate() {
    if (this.inputEl.hasAttribute("data-novalidate")) {
      this.inputEl.removeAttribute("data-novalidate");
    }

    if (this.validatePattern) {
      this.inputEl.setAttribute("pattern", this.validatePattern);
    }

    // Trigger callback, dispatch event
    this.onValidate(this.valid, this.value, this);
    this.inputEl.dispatchEvent(this.valid ? onValidEvent : onInvalidEvent);

    return this;
  }

  /**
   * Show the input by adding the active state and setting character counts (if necessary).
   */
  show() {
    addClass(this.el, "active");
    return this;
  }

  /**
   * Hide the input by removing the active state.
   */
  hide() {
    removeClass(this.el, "active");
    return this;
  }

  /**
   * Set the value of the input.
   * @param {Mixed} value
   */
  setValue(value) {
    this.value = value;

    if (value) {
      this.show();
    } else {
      this.hide();
    }

    return this;
  }

  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.value;
  }

  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.value = "";
    return this;
  }

  /**
   * Disable the input.
   */
  disable() {
    this.inputEl.attributes.setNamedItem(document.createAttribute("disabled"));
    return this;
  }

  /**
   * Enable the input.
   */
  enable() {
    this.inputEl.removeAttribute("disabled");
    return this;
  }

  /**
   * Remove typeahead
   * @param {Boolean} leaveElement Leave the element intact.
   */
  _removeTypeahead(leaveElement) {
    if (this.typeahead) {
      this.typeahead.remove(leaveElement);
    }
  }

  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    this._removeTypeahead(leaveElement);
    return super.remove(leaveElement);
  }

  /**
   * Update the element in use and the position.
   * @param {Element} el
   */
  update(el) {
    this._removeTypeahead(true);
    this.typeahead = null;
    super.update(el);
    this._initTypeAhead();

    return this;
  }
}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
CardNumber.prototype._whitelistedParams = [
  "cardTypes",
  "disableCardDefaults",
  "disableAllCardDefaults",
  "useLuhnTest",
  "onInput",
  "onBlur",
  "onFocus",
  "onValidate",
  "validate",
  "validatePattern",
  "onInvalid",
  "onValid",
  "onCardType",
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
CardNumber.prototype.defaults = {
  el: null,
  inputEl: null,
  type: null,
  cardTypes: null,
  disableCardDefaults: null,
  disableAllCardDefaults: false,
  validatePattern: null,
  useLuhnTest: true,
  onInput: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onValidate: () => {},
  onInvalid: () => {},
  onValid: () => {},
  onCardType: () => {},
};

mixin(CardNumber.prototype, messaging, validation);

export default CardNumber;
